import { PrimaryButton } from "@digipost/dds-buttons-react";
import { FC, useEffect } from "react";
import { useSelector } from "react-redux";

import sendViaApiIllustration from "../../assets/images/illustrations/signering/api-illustration.svg";
import sendFromWebIllustration from "../../assets/images/illustrations/signering/laptop.svg";
import sendViaPartnerIllustration from "../../assets/images/illustrations/signering/shaking-hands.svg";
import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../analytics/SiteImprove";
import { RegistrationStatus } from "../infrastructure/redux/org/org-actions";
import { ReduxState } from "../infrastructure/redux/state";
import { isSigneringFlow } from "../infrastructure/stateAccessors";
import Translated from "../infrastructure/Translated";
import { useRootPath } from "../infrastructure/useRootPath";
import { useTranslate } from "../infrastructure/useTranslate";

const SigneringFlowConfirmation: FC = () => {
  const signeringFlow = useSelector((state: ReduxState) =>
    isSigneringFlow(state),
  );
  const redirectUrl = useSelector((state: ReduxState) =>
    state.app.params ? state.app.params.redirectUrl : undefined,
  );
  const registrationStatus = useSelector(
    (state: ReduxState) => state.org.registrationStatus,
  );

  const translate = useTranslate();
  const rootPath = useRootPath();

  useEffect(() => {
    // Guard for tilfeldig navigering
    if (!signeringFlow && registrationStatus !== RegistrationStatus.COMPLETE) {
      window.location.href = `${rootPath}/bedrift`;
    }
  }, [registrationStatus, signeringFlow]);

  return (
    <main>
      <div className="">
        <h1 className="hw-h1 hw-color-primary-darker hw-block--mb-large-1 hw-block--mt-large-1 register-content-centered">
          <Translated textKey="register.org.confirmation_signering.heading" />
        </h1>
        <div className="">
          <h2 className="hw-h3 hw-color-primary-darker hw-block--mb-large-1 centered">
            <Translated textKey="register.signering.heading" />
          </h2>
          <div className="ways-to-send">
            <div className="ways-to-send__card-wrapper">
              <img
                className="ways-to-send__illustration"
                src={sendFromWebIllustration}
                alt={translate("register.signering.send_from_web.heading")}
              />
              <div className="ways-to-send__card">
                <h3 className="dds-subtitle-type ways-to-send__title">
                  <Translated textKey="register.signering.send_from_web.heading" />
                </h3>
                <p className="ways-to-send__description">
                  <Translated textKey="register.signering.send_from_web.ingress" />
                </p>
                <ul className="ways-to-send__description-list">
                  <li>
                    <Translated textKey="register.signering.send_from_web.list.item1" />
                  </li>
                  <li>
                    <Translated textKey="register.signering.send_from_web.list.item2" />
                  </li>
                </ul>
                <PrimaryButton
                  onClick={() => {
                    if (redirectUrl) {
                      window.location.href = redirectUrl;
                    }
                    siteImproveEvent({
                      category: SiteImproveCategories.SIGNERING_VIRKSOMHET,
                      action: SiteImproveActions.TO_SIGNERING,
                    });
                  }}
                  style={{ width: "auto" }}
                  className="ways-to-send__button"
                >
                  <Translated textKey="register.org.confirmation_signering.button" />
                </PrimaryButton>
              </div>
            </div>
            <div className="ways-to-send__card-wrapper">
              <img
                className="ways-to-send__illustration"
                src={sendViaPartnerIllustration}
                alt={translate("register.signering.send_from_partner.heading")}
              />
              <div className="ways-to-send__card">
                <h3 className="dds-subtitle-type ways-to-send__title">
                  <Translated textKey="register.signering.send_from_partner.heading" />
                </h3>
                <p className="ways-to-send__description">
                  <Translated textKey="register.signering.send_from_partner.ingress" />
                </p>
                <ul className="ways-to-send__description-list">
                  <li>
                    <Translated textKey="register.signering.send_from_partner.list.item1" />
                  </li>
                  <li>
                    <Translated textKey="register.signering.send_from_partner.list.item2" />
                  </li>
                </ul>
                <a
                  className="hw-button hw-button--outlined-primary ways-to-send__button"
                  href="mailto:salg@digipost.no?subject=Tilgang til å sende via systempartner i Posten signering"
                  onClick={() =>
                    siteImproveEvent({
                      category: SiteImproveCategories.SIGNERING_VIRKSOMHET,
                      action: SiteImproveActions.PARTNER_ACCESS,
                    })
                  }
                >
                  <Translated textKey="register.signering.contact_for_access" />
                </a>
              </div>
            </div>
            <div className="ways-to-send__card-wrapper">
              <img
                className="ways-to-send__illustration"
                src={sendViaApiIllustration}
                alt={translate("register.signering.send_from_api.heading")}
              />
              <div className="ways-to-send__card">
                <h3 className="dds-subtitle-type ways-to-send__title">
                  <Translated textKey="register.signering.send_from_api.heading" />
                </h3>
                <p className="ways-to-send__description">
                  <Translated textKey="register.signering.send_from_api.ingress" />
                </p>
                <ul className="ways-to-send__description-list">
                  <li>
                    <Translated textKey="register.signering.send_from_api.list.item1" />
                  </li>
                  <li>
                    <Translated textKey="register.signering.send_from_api.list.item2" />
                  </li>
                </ul>
                <a
                  className="hw-button hw-button--secondary ways-to-send__button"
                  href="mailto:salg@digipost.no?subject=Tilgang til API-integrasjon i Posten signering"
                  onClick={() =>
                    siteImproveEvent({
                      category: SiteImproveCategories.SIGNERING_VIRKSOMHET,
                      action: SiteImproveActions.API_ACCESS,
                    })
                  }
                >
                  <Translated textKey="register.signering.contact_for_access" />
                </a>
              </div>
            </div>
          </div>
          <p className="hw-block--mt-large-1 centered">
            <Translated textKey="register.org.confirmation_signering.digital_mailbox" />{" "}
            <a
              href="/virksomhetsadmin/"
              className="hw-link"
              onClick={() =>
                siteImproveEvent({
                  category: SiteImproveCategories.SIGNERING_VIRKSOMHET,
                  action: SiteImproveActions.TO_POSTKASSE,
                })
              }
            >
              Digipost
            </a>
            .
          </p>
        </div>
      </div>
    </main>
  );
};

export default SigneringFlowConfirmation;
